/**
 * BarbaroMojo GraphQL Client
 * Storefront API integration for BarbaroMojo.com
 * Namespace: window.BarbaroMojo
 *
 * Usage:
 *   BarbaroMojo.GraphQL.getProduct('some-handle').then(data => console.log(data));
 */
(function () {
  'use strict';

  const CONFIG = {
    apiUrl: 'https://barbaromojo-com.myshopify.com/api/2025-01/graphql.json',
    storefrontToken: '7fa51114cb21a2de617d287a58249501',
  };

  /* ── Core fetch wrapper ─────────────────────────────────── */
  async function storefront(query, variables = {}) {
    const response = await fetch(CONFIG.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API error: ${response.status} ${response.statusText}`);
    }

    const json = await response.json();

    if (json.errors) {
      console.error('[BarbaroMojo] GraphQL errors:', json.errors);
      throw new Error(json.errors.map(e => e.message).join(', '));
    }

    return json.data;
  }

  /* ── Pre-built queries ──────────────────────────────────── */
  const QUERIES = {
    productByHandle: `
      query ProductByHandle($handle: String!) {
        product(handle: $handle) {
          id
          title
          handle
          description
          descriptionHtml
          productType
          tags
          vendor
          availableForSale
          priceRange {
            minVariantPrice { amount currencyCode }
            maxVariantPrice { amount currencyCode }
          }
          images(first: 10) {
            edges {
              node {
                url
                altText
                width
                height
              }
            }
          }
          variants(first: 30) {
            edges {
              node {
                id
                title
                availableForSale
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
                selectedOptions { name value }
                image {
                  url
                  altText
                }
              }
            }
          }
        }
      }
    `,

    collectionProducts: `
      query CollectionProducts($handle: String!, $first: Int!) {
        collection(handle: $handle) {
          id
          title
          handle
          description
          image {
            url
            altText
          }
          products(first: $first) {
            edges {
              node {
                id
                title
                handle
                availableForSale
                priceRange {
                  minVariantPrice { amount currencyCode }
                }
                images(first: 1) {
                  edges {
                    node {
                      url
                      altText
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,

    predictiveSearch: `
      query PredictiveSearch($query: String!) {
        predictiveSearch(query: $query, limit: 5, types: [PRODUCT, COLLECTION, PAGE]) {
          products {
            id
            title
            handle
            availableForSale
            priceRange {
              minVariantPrice { amount currencyCode }
            }
            images(first: 1) {
              edges {
                node { url altText }
              }
            }
          }
          collections {
            id
            title
            handle
          }
          pages {
            id
            title
            handle
          }
        }
      }
    `,

    productRecommendations: `
      query ProductRecommendations($productId: ID!) {
        productRecommendations(productId: $productId) {
          id
          title
          handle
          availableForSale
          priceRange {
            minVariantPrice { amount currencyCode }
          }
          images(first: 1) {
            edges {
              node { url altText width height }
            }
          }
        }
      }
    `,

    pageByHandle: `
      query PageByHandle($handle: String!) {
        page(handle: $handle) {
          id
          title
          handle
          body
          bodySummary
          createdAt
          updatedAt
        }
      }
    `,

    blogArticles: `
      query BlogArticles($handle: String!, $first: Int!) {
        blog(handle: $handle) {
          id
          title
          articles(first: $first) {
            edges {
              node {
                id
                title
                handle
                excerpt
                content
                publishedAt
                image {
                  url
                  altText
                }
                authorV2 { name }
              }
            }
          }
        }
      }
    `,
  };

  /* ── Helper methods ─────────────────────────────────────── */
  const GraphQL = {
    /** Raw query — use for custom queries */
    query: storefront,

    /** Get a product by its URL handle */
    getProduct(handle) {
      return storefront(QUERIES.productByHandle, { handle });
    },

    /** Get products in a collection */
    getCollection(handle, first = 12) {
      return storefront(QUERIES.collectionProducts, { handle, first });
    },

    /** Predictive search */
    search(query) {
      return storefront(QUERIES.predictiveSearch, { query });
    },

    /** Get product recommendations */
    getRecommendations(productId) {
      return storefront(QUERIES.productRecommendations, { productId });
    },

    /** Get a page by handle */
    getPage(handle) {
      return storefront(QUERIES.pageByHandle, { handle });
    },

    /** Get blog articles */
    getBlogArticles(handle, first = 6) {
      return storefront(QUERIES.blogArticles, { handle, first });
    },
  };

  /* ── Expose on global namespace ─────────────────────────── */
  window.BarbaroMojo = window.BarbaroMojo || {};
  window.BarbaroMojo.GraphQL = GraphQL;

  console.log('[BarbaroMojo] GraphQL client loaded — v1.0');
})();
